import { TWidget } from 'widgets/Widget';
import { getContentByUrl } from 'widgets/toolbox/ajax';
import { TInputSelectInstance } from 'widgets/forms/InputSelect';
import { getUrlParams } from 'widgets/toolbox/util';

/**
 * @param Widget Base widget for extending
 */
function ModuleLoaderClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory global
     * @class ModuleLoader
     * @augments Widget
     * @classdesc ModuleLoader Widget - used to dynamically load content using PDX module page
     * @property {string} data-widget - Widget name `moduleLoader`
     * @property {string} data-url - URL to obtain content from server
     * @example <caption>Example of ContentLoader widget usage</caption>
     * <div
     *     data-widget="moduleLoader"
     *     data-url="${URLUtils.url('Page-IncludeHeaderMenu')}"
     * >
     * </div>
     */
    class ModuleLoader extends Widget {
        prefs() {
            return {
                initialUrl: '',
                selectedIndex: -1,
                urlParamName: 'selectedIndex',
                dataAttributeUrlParamName: 'data-module-url',
                ...super.prefs()
            };
        }

        currentUrl = '';

        selectedIndex = -1;

        selectWidget: TInputSelectInstance | undefined;

        init() {
            if (this.items.length > 0) {
                this.selectWidget = <TInputSelectInstance> this.items[0];
            }

            if (this.prefs().initialUrl) {
                getContentByUrl(this.prefs().initialUrl)
                    .then(this.updateSelection.bind(this));
            }

            if (this.prefs().selectedIndex !== -1) {
                this.updateSelectIndex(this.prefs().selectedIndex);
                this.onChange(this.refs.self);
            }

            const params = getUrlParams(window.location.href);
            const currentParam = Object.keys(params).find((key) => key.indexOf(this.prefs().urlParamName) > -1);

            if (currentParam && params[currentParam]) {
                try {
                    const index = parseInt(params[currentParam], 10);
                    const ctas = this.refs.ctaContainer.get(0)?.children;

                    if (ctas && index < ctas.length) {
                        this.selectedIndex = index;
                        this.currentUrl = this.refs.ctaContainer.get(0)?.children[index]?.getAttribute(this.prefs().dataAttributeUrlParamName)
                            || this.currentUrl;
                        getContentByUrl(this.currentUrl).then(this.updateSelection.bind(this));
                    }
                } catch (err) {
                    // eslint-disable-next-line no-console
                    console.log(`Cannot parse uri with index ${params[currentParam]}, error: ${err}`);
                }
            }
        }

        updateSelection(response: string) {
            if (this.refs.container) {
                this.refs.container.els[0].innerHTML = response;

                const ctas = this.refs.ctaContainer.get(0)?.children;

                if (ctas) {
                    for (let i = 0; i < ctas.length; i++) {
                        const child = ctas[i];

                        if (i !== this.selectedIndex) {
                            child.classList.add('m-outline');
                            child.classList.add('active');
                        } else {
                            child.classList.remove('m-outline');
                            child.classList.remove('active');
                        }
                    }
                }

                this.updateSelectIndex(this.selectedIndex);

                const url = new URL(window.location.href);

                const state = {
                    selectedIndex: this.selectedIndex.toString()
                };

                url.searchParams.set(this.prefs().urlParamName, this.selectedIndex.toString());
                window.history.pushState(state, '', url);
            }
        }

        updateSelectIndex(index) {
            if (this.selectWidget) {
                const field = this.selectWidget.ref('field').get<HTMLSelectElement>();

                if (field && field.selectedIndex !== index) {
                    field.selectedIndex = index;
                    this.selectWidget.refs.self.addClass('m-interacted');
                }
            }
        }

        /**
         * @description event handler for sorting change
         * @param el source of event
         */
        onChange(el) {
            if (el && el.length > 0 && this.items) {
                this.currentUrl = <string> this.selectWidget?.getSelectedOptions()?.els[0].getAttribute(this.prefs().dataAttributeUrlParamName);
                const num = this.selectWidget?.getSelectedOptions()?.els[0]?.getAttribute('data-item-index');

                if (num) {
                    this.selectedIndex = parseInt(num, 10);
                }

                if (this.currentUrl) {
                    getContentByUrl(this.currentUrl).then(this.updateSelection.bind(this));
                }
            }
        }

        /**
         * @description event handler for sorting change
         * @param el source of event
         * @param event Event
         */
        onCtaClick(el, event) {
            event.preventDefault();

            if (el && el.length > 0) {
                this.currentUrl = el.get(0).getAttribute(this.prefs().dataAttributeUrlParamName);
                this.selectedIndex = parseInt(el.get(0).getAttribute('data-item-index'), 10);
                getContentByUrl(this.currentUrl).then(this.updateSelection.bind(this));
            }
        }
    }

    return ModuleLoader;
}

export type TModuleLoader = ReturnType<typeof ModuleLoaderClassCreator>;

export type TModuleLoaderInstance = InstanceType<TModuleLoader>;

export default ModuleLoaderClassCreator;
